/* Scroll-triggered text reveals. See text-reveal.css for the visual side.

   The .de-anim class is NOT set here — an inline script in the layout head
   does that pre-paint, because this file is deferred and by the time it runs
   the copy has already been painted at full opacity. This script's first job
   is to set window.deTextReveal, which calls off that inline script's failsafe
   and is the signal that the reveals are in hand. */
(function () {
  'use strict';

  var root = document.documentElement;
  var SELECTOR = '[data-reveal]';

  // Must match the per-step values in text-reveal.css.
  var WORD_STAGGER = 45;
  var ITEM_STAGGER = 90;
  // How long after one reveal starts its tail before the next begins. Deliberately
  // shorter than the 700ms transition, so steps overlap instead of queueing — a
  // five-word heading would otherwise hold its copy back by most of a second.
  var HANDOFF = 250;

  // A reveal is sequenced against its neighbours inside one of these.
  var GROUP = '[data-reveal-group], .shopify-section, section, footer';

  // Tells the layout's failsafe that this file loaded and will take over.
  window.deTextReveal = true;

  function reveal(el) {
    el.setAttribute('data-reveal-in', '');
    // Longest cue + the slowest transition (the 2s slide), then release the hint.
    // Dropping will-change while something is still moving costs the compositing
    // it was asked for, so this has to outlast the longest animation on the page.
    window.setTimeout(function () {
      el.setAttribute('data-reveal-done', '');
    }, 4000);
  }

  function splitWords(node) {
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        if (!child.textContent.trim()) return;

        // Capturing split keeps the whitespace runs, so words stay separated
        // and existing line breaks are untouched.
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
            return;
          }
          var word = document.createElement('span');
          word.className = 'de-reveal__word';
          // The heading's aria-label carries the text; the pieces are hidden so a
          // screen reader does not step through the heading one word at a time.
          word.setAttribute('aria-hidden', 'true');
          word.textContent = part;
          frag.appendChild(word);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        // Recurse through inline markup; <br> is left exactly where it is,
        // because the headings rely on it for their line breaks.
        splitWords(child);
      }
    });
  }

  function init() {
    var targets = Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
    if (!targets.length) return;

    // Reduced motion, or a browser without IntersectionObserver: show the text
    // as-is rather than waiting for a trigger that will never fire.
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      root.classList.remove('de-anim');
      return;
    }

    // Bucket the targets by section, preserving document order, so each section
    // can sequence its own reveals without reaching into its neighbours.
    var groups = [];
    targets.forEach(function (el) {
      var owner = el.closest(GROUP) || document.body;
      var group = null;
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].owner === owner) {
          group = groups[i];
          break;
        }
      }
      if (!group) {
        group = { owner: owner, items: [] };
        groups.push(group);
      }
      group.items.push(el);
    });

    groups.forEach(function (group) {
      // Running start offset: each reveal hands the next one its cue.
      var base = 0;

      group.items.forEach(function (el) {
        var mode = el.getAttribute('data-reveal');
        el.style.setProperty('--de-reveal-base', base + 'ms');

        if (mode === 'slide-right') {
          // Measured per element rather than a flat percentage: the distance is
          // whatever puts its left edge clear of the viewport, so something
          // already bleeding off the right travels no further than it needs to.
          // The sections that use this clip, so the offset never widens the page.
          var rect = el.getBoundingClientRect();
          el.style.setProperty(
            '--de-reveal-slide',
            Math.ceil(window.innerWidth - rect.left + 24) + 'px'
          );
          el.setAttribute('data-reveal-ready', '');
          base += HANDOFF;
          return;
        }

        if (mode === 'words') {
          // The sections write the label server-side; this covers any heading
          // that reaches the splitter without one, before its text is broken up.
          if (!el.hasAttribute('aria-label')) {
            el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
          }
          splitWords(el);
          var words = el.querySelectorAll('.de-reveal__word');
          if (!words.length) return;
          Array.prototype.forEach.call(words, function (word, i) {
            word.style.setProperty('--de-reveal-i', i);
          });
          // Only now is it safe for the CSS to hide the words.
          el.setAttribute('data-reveal-split', '');
          base += (words.length - 1) * WORD_STAGGER + HANDOFF;
        } else if (mode === 'stagger') {
          var kids = el.children;
          if (!kids.length) return;
          Array.prototype.forEach.call(kids, function (kid, i) {
            kid.style.setProperty('--de-reveal-i', i);
          });
          el.setAttribute('data-reveal-ready', '');
          base += (kids.length - 1) * ITEM_STAGGER + HANDOFF;
        } else {
          el.setAttribute('data-reveal-ready', '');
          base += HANDOFF;
        }
      });
    });

    // A slide-right element is staged clear of the viewport, so it can never
    // intersect it and would wait for a cue that never comes. Its cue is taken
    // from its parent instead, which stays where the layout put it.
    //
    // The first one in a section also lends its cue to the rest, so a staggered
    // pair starts one clock together and the running delays hold between them.
    // Left to their own parents they would each fire whenever they happened to
    // scroll in — in the about section that is a few hundred pixels apart.
    var cues = [];
    var pairs = targets.map(function (el) {
      var watch = el;
      if (el.getAttribute('data-reveal') === 'slide-right') {
        var owner = el.closest(GROUP) || document.body;
        var cue = null;
        for (var i = 0; i < cues.length; i++) {
          if (cues[i].owner === owner) {
            cue = cues[i];
            break;
          }
        }
        if (cue) {
          watch = cue.watch;
        } else {
          watch = el.parentElement || el;
          cues.push({ owner: owner, watch: watch });
        }
      }
      return { watch: watch, target: el, done: false };
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var fired = false;
          pairs.forEach(function (pair) {
            if (pair.done || pair.watch !== entry.target) return;
            pair.done = true;
            fired = true;
            reveal(pair.target);
          });
          // Play once: stop watching as soon as it has fired.
          if (fired) observer.unobserve(entry.target);
        });
      },
      // Fires a little after the element's top edge clears, so a reveal is
      // never spent on something only a few pixels into view.
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );

    pairs.forEach(function (pair) {
      observer.observe(pair.watch);
    });
  }

  function start() {
    try {
      init();
    } catch (e) {
      // Any failure here must not leave copy stuck at opacity 0.
      root.classList.remove('de-anim');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
